import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../../../core/services/breadcrumb/breadcrumb.service';
import { EmailService } from '../../../../../services/email.service';
import { EmailLog } from '../../../../../models/Email';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'app-email-details',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    CardModule,
    ButtonModule,
    TagModule,
    TableModule,
    DividerModule
  ],
  providers: [MessageService],
  templateUrl: './email-details.component.html',
  styleUrl: './email-details.component.css',
})
export class EmailDetailsComponent implements OnInit {
  emailLog?: EmailLog;
  emailId?: number;
  loading = true;

  // Constantes de mapeo de estados
  private readonly STATUS_SEVERITY_MAP: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
    'sent': 'success',
    'queued': 'warning',
    'failed': 'danger',
    'partially_failed': 'warning'
  };

  private readonly STATUS_LABEL_MAP: Record<string, string> = {
    'queued': 'En cola',
    'sent': 'Enviado',
    'failed': 'Fallido',
    'partially_failed': 'Parcialmente fallido'
  };

  private readonly DELIVERY_STATUS_SEVERITY_MAP: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
    'sent': 'success',
    'pending': 'warning',
    'failed': 'danger'
  };

  private readonly DELIVERY_STATUS_LABEL_MAP: Record<string, string> = {
    'pending': 'Pendiente',
    'sent': 'Enviado',
    'failed': 'Fallido'
  };

  private readonly FILTER_TYPE_LABEL_MAP: Record<string, string> = {
    'manual': 'Selección Manual',
    'course_call': 'Convocatoria de Curso',
    'proof': 'Convocatoria de Examen',
    'product': 'Producto Contratado'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private emailService: EmailService,
    private spinner: NgxSpinnerService,
    private messageService: MessageService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit() {
    this.emailId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.emailId) {
      this.breadcrumbService.setBreadcrumb({
        display_name: 'Detalles del Email',
        url: `/dashboard/email/show/${this.emailId}`,
        items: [
          { name: 'Comunicaciones', url: '/dashboard/comunicaciones' },
          { name: 'Detalles del Email', url: `/dashboard/email/show/${this.emailId}` }
        ]
      });
      this.loadEmailDetails();
    }
  }

  loadEmailDetails() {
    this.executeWithSpinner(
      () => this.emailService.getEmailDetails(this.emailId!),
      (response) => {
        this.emailLog = response.data;
        this.loading = false;
      },
      () => {
        this.showToast('error', 'Error', 'No se pudo cargar los detalles del email');
        this.loading = false;
      }
    );
  }

  getStatusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    return this.STATUS_SEVERITY_MAP[status] || 'info';
  }

  getStatusLabel(status: string): string {
    return this.STATUS_LABEL_MAP[status] || status;
  }

  getDeliveryStatusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    return this.DELIVERY_STATUS_SEVERITY_MAP[status] || 'info';
  }

  getDeliveryStatusLabel(status: string): string {
    return this.DELIVERY_STATUS_LABEL_MAP[status] || status;
  }

  getFilterTypeLabel(filterType: string): string {
    return this.FILTER_TYPE_LABEL_MAP[filterType] || filterType;
  }

  goBack() {
    this.router.navigate(['/dashboard/comunicaciones']);
  }

  retryEmail() {
    if (!this.emailId) return;

    this.executeWithSpinner(
      () => this.emailService.retryEmail(this.emailId!),
      (response) => {
        this.showToast('success', 'Éxito', response.message || 'Email reenviado a la cola correctamente');
        this.loadEmailDetails();
      },
      (error) => {
        this.showToast('error', 'Error', error.error?.message || 'No se pudo reintentar el envío');
      }
    );
  }

  /**
   * Ejecuta una operación con spinner y manejo de errores
   */
  private executeWithSpinner<T>(
    operation: () => any,
    onSuccess: (response: any) => void,
    onError: (error: any) => void
  ): void {
    this.spinner.show();
    operation().subscribe({
      next: (response: any) => {
        onSuccess(response);
        this.spinner.hide();
      },
      error: (error: any) => {
        onError(error);
        this.spinner.hide();
      }
    });
  }

  /**
   * Muestra un mensaje toast
   */
  private showToast(severity: 'success' | 'error' | 'info' | 'warn', summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail });
  }
}
